import React, { useEffect, useState, useRef, memo } from 'react';
import { formatNumber } from '@/utils/formatters';

/**
 * DailyPulseTicker — v3.6 Social Loop Engineering
 *
 * An unobtrusive "Live Feed" fixed to the bottom-left corner of the page.
 * Shows recent leaderboard activity to create the "Fringe Effect":
 * "Other people are playing right now — this game is alive."
 *
 * Design:
 * - Fixed bottom-left, small pill shape, non-blocking
 * - Cycles through top players every 4 seconds
 * - Fades in/out between entries
 * - Auto-hides after 30 seconds (doesn't become annoying)
 * - Respects prefers-reduced-motion
 */

interface LeaderboardEntry {
  userId?: number;
  name?: string;
  score: number;
  lastPlayedAt?: string;
}

interface DailyPulseTickerProps {
  topScores: LeaderboardEntry[];
  isLoading?: boolean;
}

// Synthetic activity messages to mix with real scores
function buildMessages(topScores: LeaderboardEntry[]): string[] {
  const msgs: string[] = [];
  topScores.slice(0, 5).forEach((entry, i) => {
    const name = (entry.name ?? 'Anonymous').split(' ')[0];
    const score = formatNumber(entry.score);
    if (i === 0) msgs.push(`🏆 ${name} is leading with ${score} pts`);
    else if (i === 1) msgs.push(`⭐ ${name} just hit ${score}!`);
    else msgs.push(`🦕 ${name} scored ${score} — can you beat it?`);
  });
  if (msgs.length === 0) {
    msgs.push('🦕 Be the first to set a record today!');
  }
  return msgs;
}

export const DailyPulseTicker = memo(({ topScores, isLoading }: DailyPulseTickerProps) => {
  const [msgIdx, setMsgIdx] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [visible, setVisible] = useState(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const messages = buildMessages(topScores);

  // Cycle messages with fade transition
  useEffect(() => {
    if (messages.length <= 1) return;
    const interval = setInterval(() => {
      // Fade out
      setOpacity(0);
      setTimeout(() => {
        setMsgIdx((i) => (i + 1) % messages.length);
        setOpacity(1);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, [messages.length]);

  // Auto-hide after 30 seconds
  useEffect(() => {
    hideTimer.current = setTimeout(() => setVisible(false), 30_000);
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  if (!visible || isLoading || messages.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '16px',
        left: '16px',
        zIndex: 9998,
        maxWidth: '260px',
        pointerEvents: 'none', // never blocks clicks
      }}
      aria-live="polite"
      aria-label="Live player activity"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          padding: '7px 12px',
          background: 'rgba(255,255,255,0.95)',
          border: '1px solid #e5e7eb',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          opacity,
          transition: 'opacity 300ms ease',
          fontFamily: 'system-ui, -apple-system, Roboto, Arial, sans-serif',
        }}
      >
        {/* Pulse dot */}
        <span
          style={{
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            background: '#22c55e',
            flexShrink: 0,
            animation: 'pulse 2s ease-in-out infinite',
          }}
        />
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(0.85); }
          }
          @media (prefers-reduced-motion: reduce) {
            .daily-pulse-dot { animation: none !important; }
          }
        `}</style>
        <span style={{ fontSize: '11px', color: '#374151', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {messages[msgIdx]}
        </span>
      </div>
    </div>
  );
});

DailyPulseTicker.displayName = 'DailyPulseTicker';
